import React, { useState, useMemo, useRef } from 'react';
import { Report, ProjectGlobalData, IntervalConfig, UserProfile, ServiceItem, IntervalType, Note, ProjectStatus, ServiceAssignment } from '../../types';
import { Clock, Wallet, History, Plus, MessageSquare, Send, User, Calendar, CalendarClock, Check, UserCheck, Settings2, AtSign, Link as LinkIcon, Trash2, ListChecks } from 'lucide-react';
import { hatBestandteile } from '../../services/bestandteile/bestandteilLogic';
import type { BestandteileKontext } from '../bestandteile/BestandteileDialog';
import { plaketteFuer, PLAKETTEN_KLASSE, stundenPlaketteFuer, type OffenUeberschreibung } from '../bestandteile/bestandteileDialogLogic';
import { addCustomDateRange, emptyCustomDateRangeDraft, isValidCustomDateRangeDraft, removeCustomDateRange, resolveServiceLogSource } from './projectHubLogic';
import { Button } from '../Button';
import { ClampedText } from '../ClampedText';
import { calculateNextDueDate, getNextOccurrenceOf } from '../../services/dateService';
import { ServiceDetailsModal } from '../ServiceDetailsModal';
import { NotesModal } from '../NotesModal';
import { downloadIcs } from '../../services/calendarService';
import { createNotificationsForMentions } from '../../services/cloudService';
import { extractMentionedIds } from '../../services/mentionUtils';
import { applyRepresentatives, getRepresentatives } from '../../services/projectRepresentatives';
import { ausfuehrungsStellvertreter, teileBegehungstag, type BegehungstagPerson } from './begehungstageAufteilen';
import { EmailModal } from '../EmailModal';
import { readOnlyMessage } from '../../services/readOnlyNotice';
import { isAdminRole } from '../../services/projectLockLogic';
import { useRechte } from '../../hooks/useRechte';
import { anker } from '../../tutorial/anker';
import { ArchivDokumenteDialog } from '../dokumente/ArchivDokumenteDialog';
import { DokumenteArchivEntscheidung } from '../dokumente/types';
import { FlatMonthBookingDialog } from '../FlatMonthBookingDialog';
import { suggestFlatMonths } from '../../services/flatRateBooking';
import { flatCoverageHints } from '../../services/flatRateLogic';
import { isNepService, NEP_BADGE, NEP_HINT } from '../../services/nepPosition';
import { assignmentChangeNoteText, changeServiceAssignment } from '../../services/assignmentChange';
import { anteil, leistungsSumme } from './projektStand';
import { pruefeArchivierbarkeit } from '../../services/archiveGuardService';
import { SifaKennzahlen } from '../../services/sifa/betreuungTypes';
import { SifaBetriebsdatenKarte } from './SifaBetriebsdatenKarte';
import { planeStatusWechsel } from './sifaBetriebsdatenLogic';

interface Props {
  report: Report;
  globalData: ProjectGlobalData;
  /**
   * Bei SiGeKo/HSE immer gesetzt (ProjectHub gibt die Weekly-Vorgabe mit).
   * Bei SiFa kommt exakt `metadata.intervalConfig` — meist undefined: eine
   * Betreuung hat keinen Begehungsturnus, und die Karte „Zeitplan & Intervall"
   * wird dort durch die Betreuungskarte ersetzt.
   */
  intervalConfig?: IntervalConfig;
  /** Kennzahlen der SiFa-Betreuung (useSifaHubData.kennzahlen) — nur bei SiFa gesetzt. */
  sifaKennzahlen?: SifaKennzahlen;
  currentUser: UserProfile | null;
  availableUsers: UserProfile[];
  onUpdate: (report: Report) => void;
  onShowPerformanceModal: () => void;
  onShowHistoryModal: () => void;
  /** Ohne Angabe (Handy-App) entfaellt das Buchen; die Anzeige bleibt. */
  onTrackService?: (service: ServiceItem) => void;
  /**
   * MONATSPAUSCHALE: einen offenen Monat aus dem Vorschlag buchen
   * (useReportActions.handleBookFlatMonth). Optional, weil nicht jeder
   * Einbettungsort den Buchungspfad hat — ohne Handler bleibt der ganze
   * Vorschlagsblock weg (ein Vorschlag ohne Knopf waere nur Raetsel).
   */
  onBookFlatMonth?: (flatServiceId: string, monthKey: string, quantity: number) => Promise<{ ok: boolean; message: string }>;
  canSeeFinancials: boolean;
  linkedParent?: Report | null;
  isReadOnly?: boolean;
  /**
   * PAKETPOSITION (Position mit Bestandteilen, docs/positionsbestandteile-plan.md
   * B3): ein Klick oeffnet den Bestandteile-Dialog statt der Schnellbuchung —
   * Pakete bucht nur der Bestandteile-Weg (Kernregel 7). Ansehen geht auch in
   * der Nur-Ansicht; ob gebucht werden darf, entscheidet der Dialog.
   */
  onOpenBestandteile?: (service: ServiceItem) => void;
  /** Fuer den Abschnitt „Bestandteile" in der Detailansicht der Position. */
  bestandteileKontext?: BestandteileKontext;
  /** Lokale Ueberschreibung des Stands aus dem Hub — nur fuer die Plakette. */
  bestandteileOffen?: OffenUeberschreibung;
}

const WEEKDAYS = [
    { label: 'Mo', value: 1 }, { label: 'Di', value: 2 }, { label: 'Mi', value: 3 },
    { label: 'Do', value: 4 }, { label: 'Fr', value: 5 }, { label: 'Sa', value: 6 }, { label: 'So', value: 0 }
];

export const ProjectStats: React.FC<Props> = ({
  report, globalData, intervalConfig: intervalConfigProp, sifaKennzahlen, currentUser, availableUsers,
  onUpdate, onShowHistoryModal, onTrackService, onBookFlatMonth,
  canSeeFinancials, linkedParent, isReadOnly = false,
  onOpenBestandteile, bestandteileKontext, bestandteileOffen
}) => {
  const istSifa = report.metadata.projectType === 'sifa';
  // Rollensystem (docs/rollensystem-plan.md §4, nur Oberfläche): Archivieren
  // und aus dem Archiv holen darf, wer das Recht `projekte.archivieren` hat —
  // admin und super_admin immer, ohne Rechte aus der Datenbank nur sie.
  const rechte = useRechte(currentUser?.role);
  const darfArchivieren = isAdminRole(currentUser?.role) || rechte.hat('projekte.archivieren');
  // Nur fuer die ANZEIGE der SiGeKo-Karte (Frequenz, Wochenplan, Termine).
  // Geschrieben wird intervalConfig ausschliesslich in Pfaden, die bei SiFa
  // nicht gerendert werden — und im Statuswechsel ueber planeStatusWechsel,
  // das die echte Prop (intervalConfigProp) bekommt, nie diese Vorgabe.
  const intervalConfig: IntervalConfig = intervalConfigProp ?? { type: 'weekly' };
  const [customDays, setCustomDays] = useState(intervalConfig.customDays || 14);
  const [newNote, setNewNote] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  // Die Archiv-Rueckfrage nach der Dokumentenablage. Offen heisst: der
  // Statuswechsel auf 'finished' wartet auf die Entscheidung.
  const [archivDialogOffen, setArchivDialogOffen] = useState(false);
  // Der Archiv-Riegel fragt die Datenbank nach den Toechtern. Solange das
  // laeuft, bleibt der Statusumschalter gesperrt — ein zweiter Klick wuerde
  // sonst eine zweite Pruefung starten und am Ende ZWEI Dialoge oeffnen.
  const [archivPruefungLaeuft, setArchivPruefungLaeuft] = useState(false);
  const [beendenRueckfrage, setBeendenRueckfrage] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [precapturedHours] = useState<number>(1);
  const [precapturedServiceId] = useState<string | null>(null);
  const [selectedAddons] = useState<Record<string, number>>({});

  // Quick Mention State — no more quickMentionedIds Set
  const [showQuickMentions, setShowQuickMentions] = useState(false);
  const [quickMentionQuery, setQuickMentionQuery] = useState('');
  const quickInputRef = useRef<HTMLInputElement>(null);

  const [newRange, setNewRange] = useState(emptyCustomDateRangeDraft);

  const meta = report.metadata;
  // Stellvertreter im Wochenplan = Ausführungs-Vertreter: Die Verantwortliche
  // Planungsphase steht zwar in der Liste, macht aber keine Begehungen und
  // bekommt deshalb weder Begehungstage noch einen „nächsten Termin".
  const deputies = ausfuehrungsStellvertreter(meta).map(slot => slot.rep);
  const dep1 = deputies[0];
  const dep2 = deputies[1];
  // In der Liste stehen heisst zuständig — einen separaten Aktiv-Haken gibt es nicht mehr.
  const hasDeputy1 = !!dep1?.name;
  const hasDeputy2 = !!dep2?.name;
  
  const effectiveGlobalData = linkedParent ? linkedParent.metadata.globalData : globalData;
  // Memoisiert, weil zwei useMemo-Hooks (Mengen-Fortschritt und
  // Monatsvorschlag) auf der Liste haengen — ein `|| []` je Render waere dort
  // jedes Mal eine neue Referenz.
  const effectiveServices = useMemo(() => effectiveGlobalData?.services || [], [effectiveGlobalData]);

  // MONATSPAUSCHALE: der ruhige Hinweis je gedeckter Position. Frisch aus der
  // Liste gerechnet (kein Deckungsstempel auf dem rohen globalData-Bestand,
  // siehe flatCoverageHints) — rein Anzeige, der Schnellzugriff bucht weiter
  // ganz normal: gedeckt ist das Geld, nicht die Leistung.
  const flatHints = useMemo(() => flatCoverageHints(effectiveServices), [effectiveServices]);

  // Protokoll fuer die Detailansicht je Position — dieselbe Quelle wie der
  // Buchungsdialog (resolveServiceLogsForService), nur ungefiltert.
  const detailServiceLog = useMemo(
    () => (showDetailModal ? resolveServiceLogSource(globalData, linkedParent || null) : []),
    [showDetailModal, globalData, linkedParent]
  );

  // MONATSPAUSCHALE: die offenen Monate des Vorschlags ("08/2026 noch nicht
  // gebucht"). Reine Ableitung aus Kalender + Laufzeit + FLAT-Schluesseln
  // (flatRateBooking) — hier wird NIE gebucht, erst der Dialog bucht auf
  // Klick (Betreiber-Entscheidung 4). Beim Tochterprojekt rechnen Mutter-LV
  // und Mutter-Protokoll, denn dort lebt die Pauschalposition.
  const heute = new Date().toISOString().slice(0, 10);
  const flatMonthSuggestions = useMemo(
    () => suggestFlatMonths({
      services: effectiveServices,
      serviceLog: effectiveGlobalData?.serviceLog || [],
      startDate: effectiveGlobalData?.startDate,
      endDate: effectiveGlobalData?.endDate,
      today: heute,
    }),
    [effectiveServices, effectiveGlobalData, heute]
  );
  // Der Monat, den der Nutzer angeklickt hat — oeffnet den Buchungsdialog.
  const [flatBookingMonthKey, setFlatBookingMonthKey] = useState<string | null>(null);

  // Prop-Änderung übernehmen, ohne eine lokale Eingabe zu überschreiben.
  const [syncedCustomDays, setSyncedCustomDays] = useState(intervalConfig.customDays);
  if (syncedCustomDays !== intervalConfig.customDays) {
      setSyncedCustomDays(intervalConfig.customDays);
      if (intervalConfig.customDays) setCustomDays(intervalConfig.customDays);
  }

  const visibleNotes = useMemo(() => {
      const notes = globalData.notes || [];
      if (canSeeFinancials) return notes;
      return notes.map(n => ({
          ...n,
          text: n.text.replace(/\b\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?\s?(?:€|EUR)/gi, '*** €')
      }));
  }, [globalData.notes, canSeeFinancials]);

  const handleIntervalChange = (type: IntervalType, days?: number) => {
    const daysToUse = days !== undefined ? days : (type === 'custom' ? customDays : undefined);
    if (type === 'custom' && days !== undefined) setCustomDays(days);
    const newConfig: IntervalConfig = { ...intervalConfig, type, customDays: type === 'custom' ? daysToUse : undefined };
    const baseDate = report.metadata.date || new Date().toISOString().split('T')[0];
    const nextDateStr = calculateNextDueDate(baseDate, newConfig);
    onUpdate({ ...report, metadata: { ...report.metadata, intervalConfig: { ...newConfig, nextScheduledDate: nextDateStr } } });
  };

  type DateField = 'startDate' | 'endDate' | 'onHoldUntil'
    | 'planningStartDate' | 'planningEndDate'
    | 'executionStartDate' | 'executionEndDate';

  const handleDateChange = (field: DateField, value: string) => {
      onUpdate({ ...report, metadata: { ...report.metadata, globalData: { ...globalData, [field]: value } } });
  };

  const applyGlobalData = (patch: Partial<ProjectGlobalData>) => {
      onUpdate({ ...report, metadata: { ...report.metadata, globalData: { ...globalData, ...patch } } });
  };

  const handleAddCustomRange = () => {
      if (!isValidCustomDateRangeDraft(newRange)) return;
      applyGlobalData({ customDateRanges: addCustomDateRange(globalData.customDateRanges, newRange, crypto.randomUUID()) });
      setNewRange(emptyCustomDateRangeDraft());
  };

  const handleRemoveCustomRange = (id: string) => {
      applyGlobalData({ customDateRanges: removeCustomDateRange(globalData.customDateRanges, id) });
  };

  /**
   * Der Statuswechsel selbst — ohne jede Rueckfrage.
   *
   * `dokumenteEntscheidung` wird nur beim Abschliessen mitgegeben und haelt
   * fest, ob die Dokumentenablage behalten oder geraeumt wurde. Der Vermerk
   * beantwortet spaeter die Frage, wo die Plaene geblieben sind — sonst
   * koennte das niemand mehr klaeren.
   */
  const applyStatusChange = (newStatus: string, dokumenteEntscheidung?: DokumenteArchivEntscheidung | null) => {
      // D1-Fix (docs/sifa-betreuung-plan.md): bei SiFa bleibt intervalConfig
      // EXAKT, was es war — der Schluessel wird gar nicht erst angefasst
      // (undefined bleibt undefined). Bisher bekam jedes SiFa-Projekt hier die
      // Weekly-Vorgabe des Hubs und stand ab dem Folgetag als ueberfaellig in
      // jeder Liste. SiGeKo/HSE laufen unveraendert ueber denselben Plan.
      const plan = planeStatusWechsel({
          istSifa,
          bisherigerStatus: globalData.status,
          neuerStatus: newStatus as ProjectStatus,
          startDate: globalData.startDate,
          intervalConfig: intervalConfigProp,
          heute: new Date().toISOString().split('T')[0],
      });
      const updatedGlobalData: ProjectGlobalData = { ...globalData, ...plan.globalDataPatch };
      if (dokumenteEntscheidung) updatedGlobalData.documentsArchiveDecision = dokumenteEntscheidung;
      onUpdate({
          ...report,
          metadata: plan.schreibeIntervalConfig
              ? { ...report.metadata, globalData: updatedGlobalData, intervalConfig: plan.intervalConfig }
              : { ...report.metadata, globalData: updatedGlobalData },
      });
  };

  const handleStatusChange = async (newStatus: string) => {
      // Zweite Reihe gegen den Lesemodus. Bisher fehlte sie hier ganz und der
      // Schutz griff erst spaet in App.handleUpdateReport — das genuegte,
      // solange ein Statuswechsel nur ein Feld umsetzte. Seit am Abschliessen
      // das Loeschen von DATEIEN haengen kann, muss der Riegel vor dem Dialog
      // sitzen: gegen einen Storage-Aufruf hilft der spaetere Wachposten in
      // App.tsx nicht mehr.
      if (isReadOnly) {
          alert(readOnlyMessage('Der Projektstatus wurde nicht geändert.'));
          return;
      }
      // Steht der Status schon auf 'finished', ist nichts zu tun. Ohne diesen
      // Kurzschluss oeffnete ein erneuter Klick auf „Archiv" die Rueckfrage
      // wieder — und damit waere das Raeumen der Ablage dauerhaft aus dem
      // Projekt heraus erreichbar statt, wie ueberall beschrieben, genau
      // einmal beim Archivieren.
      if (newStatus === globalData.status) return;
      // ARCHIV ist Verwaltungssache (Betreiberentscheidung 15.09.2026): nur
      // wer das Recht „Projekt archivieren" hat (Administratoren immer), legt
      // ein Projekt ins Archiv oder holt es wieder heraus. Mitarbeiter setzen
      // BEENDET — damit erscheint das Projekt in der Buchhaltung als faellig
      // fuer die Schlussrechnung.
      if (globalData.status === 'finished' && !darfArchivieren) {
          alert('Dieses Projekt liegt im Archiv. Herausholen kann es nur, wer das Recht „Projekt archivieren“ hat.');
          return;
      }
      if (newStatus === 'finished') {
          if (!darfArchivieren) {
              alert('Archivieren kann nur, wer das Recht „Projekt archivieren“ hat. Bitte das Projekt auf „Beendet" setzen — die Buchhaltung stellt dann die Schlussrechnung.');
              return;
          }
          if (globalData.status !== 'completed') {
              alert('Ins Archiv geht ein Projekt nur aus „Beendet" heraus: erst beenden, dann Schlussrechnung stellen und bezahlen lassen, dann archivieren.');
              return;
          }
          // ARCHIV-RIEGEL: ein Projekt mit offenem Posten bleibt offen — und
          // eine Mutter mit offenen Toechtern ebenfalls. Und seit Regel 3:
          // die Schlussrechnung muss gestellt und bezahlt sein.
          //
          // Er steht VOR der Dokumenten-Rueckfrage, aus demselben Grund, aus
          // dem der isReadOnly-Riegel oben nach vorn gezogen wurde: an dem
          // Dialog haengt das LOESCHEN der Dateiablage. Ein Riegel dahinter
          // koennte das Archivieren zwar noch verhindern, die Ablage waere
          // aber schon geraeumt.
          //
          // Warum das Geld kostet und wie "offen" gemeint ist: siehe
          // services/archiveGuardLogic.ts.
          setArchivPruefungLaeuft(true);
          let urteil;
          try {
              urteil = await pruefeArchivierbarkeit(globalData.projectId || '');
          } finally {
              setArchivPruefungLaeuft(false);
          }
          if (!urteil.erlaubt) {
              alert(urteil.meldung);
              return;
          }
          // Die Ablage haengt an der Projekt-Id in der Datenbank. Fehlt sie
          // (frisch angelegtes, noch nie gespeichertes Projekt), gibt es auch
          // keine Ablage — dann bleibt es bei der schlichten Rueckfrage.
          const projektId = globalData.projectId;
          if (projektId && projektId !== 'temp-id') {
              setArchivDialogOffen(true);
              return;
          }
          if (!window.confirm("Sind Sie sicher, dass Sie das Projekt archivieren möchten?")) return;
      }
      // BEENDET nimmt das Projekt aus der Projektliste (ProjectOverview) —
      // ein Mitarbeiter findet es danach nicht wieder. Deshalb fragen.
      if (newStatus === 'completed' && globalData.status !== 'finished') {
          setBeendenRueckfrage(true);
          return;
      }
      applyStatusChange(newStatus);
  };

  // Der Statusumschalter zeigt „Archiv" nur, wo es etwas zu tun gibt: liegt
  // das Projekt schon dort (damit der Zustand sichtbar ist), oder ist es
  // beendet und der Betrachter darf archivieren.
  const zeigeArchivOption = globalData.status === 'finished'
      || (darfArchivieren && globalData.status === 'completed');
  const statusOptionen: ProjectStatus[] = zeigeArchivOption
      ? ['planning', 'active', 'on_hold', 'completed', 'finished']
      : ['planning', 'active', 'on_hold', 'completed'];
  const statusHinweis = globalData.status === 'completed'
      ? (darfArchivieren
          ? 'Beendet — die Buchhaltung stellt die Schlussrechnung. Ins Archiv geht das Projekt, sobald sie gestellt und bezahlt ist.'
          : 'Beendet — nicht mehr in der Projektliste; die Buchhaltung stellt die Schlussrechnung. Archivieren kann anschließend, wer das Recht „Projekt archivieren“ hat.')
      : globalData.status === 'finished'
          ? (darfArchivieren
              ? 'Archiviert — Schlussrechnung gestellt und bezahlt.'
              : 'Archiviert — herausholen kann es nur, wer das Recht „Projekt archivieren“ hat.')
          : null;

  const handleWeekdaySplit = (person: BegehungstagPerson, day: number) => {
      // Rechnet über die VOLLE Liste und schreibt auf die Originalpositionen
      // der Ausführungs-Vertreter zurück — Planungs-Einträge bleiben
      // unverändert stehen (begehungstageAufteilen.ts).
      const { ownerDays, reps: nextReps } = teileBegehungstag(
          getRepresentatives(meta), intervalConfig.preferredWeekdays || [], person, day
      );

      // Wochentage der Stellvertreter laufen über die Vertreterliste; die
      // Legacy-Felder spiegelt applyRepresentatives mit, sonst liefe die Liste
      // gegen die Legacy-Felder auseinander.
      const nextMeta = nextReps ? applyRepresentatives(meta, nextReps) : meta;
      onUpdate({
          ...report,
          metadata: ownerDays
              ? { ...nextMeta, intervalConfig: { ...intervalConfig, preferredWeekdays: ownerDays } }
              : nextMeta
      });
  };

  // ── QUICK NOTE — Fixed mention logic ──
  const handleQuickInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setNewNote(val);
      const words = val.split(' ');
      const lastWord = words[words.length - 1];
      if (lastWord.startsWith('@') && availableUsers.length > 0) {
          setShowQuickMentions(true);
          setQuickMentionQuery(lastWord.slice(1).toLowerCase());
      } else {
          setShowQuickMentions(false);
      }
  };

  const handleQuickMentionSelect = (user: UserProfile) => {
      const words = newNote.split(' ');
      words.pop();
      const name = `${user.first_name} ${user.last_name}`;
      setNewNote([...words, `@${name} `].join(' '));
      // FIX: No more Set-based tracking — we extract from text on submit
      setShowQuickMentions(false);
      quickInputRef.current?.focus();
  };

  const handleAddNoteLocal = async () => {
      if (!newNote.trim()) return;
      // Zweite Reihe: der Composer ist im Lesemodus ausgeblendet, aber auch
      // dieser Pfad wuerde sonst Benachrichtigungen ohne Notiz erzeugen.
      if (isReadOnly) {
          alert(readOnlyMessage('Die Notiz wurde nicht gespeichert und es wurde niemand benachrichtigt.'));
          return;
      }

      // FIX: Extract mentions from final text — single source of truth
      // This prevents cross-matching (e.g. all "Ehlers" getting notified)
      const mentionsList = extractMentionedIds(newNote, availableUsers);
      
      const note: Note = { 
          id: crypto.randomUUID(), 
          text: newNote, 
          date: new Date().toISOString(), 
          author: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Benutzer', 
          readBy: [currentUser?.id || ''],
          mentions: mentionsList
      };
      
      onUpdate({ ...report, metadata: { ...meta, globalData: { ...globalData, notes: [note, ...(globalData.notes || [])] } } });
      
      if (mentionsList.length > 0) {
          await createNotificationsForMentions(
            mentionsList,
            note.id,
            newNote,
            globalData.projectId || 'unknown',
            report.metadata.projectName,
            currentUser?.id || 'system',
            currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'System'
         );
      }

      setNewNote('');
  };

  // FIX: Use startsWith instead of includes to prevent partial matching
  const filteredQuickUsers = availableUsers.filter(u => {
      const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
      const firstName = (u.first_name || '').toLowerCase();
      const lastName = (u.last_name || '').toLowerCase();
      const q = quickMentionQuery.toLowerCase();
      return firstName.startsWith(q) || lastName.startsWith(q) || fullName.startsWith(q);
  });

  // FINANCIALS — dieselbe Rechnung wie der Projektstand (projektStand.ts),
  // damit Balken oben und Karte hier nie zwei Zahlen zeigen.
  const summe = useMemo(() => leistungsSumme(effectiveServices), [effectiveServices]);
  const financials = { totalContracted: summe.totalContracted, totalUsed: summe.totalUsed };
  const budgetPercent = anteil(summe.totalUsed, summe.totalContracted);
  const currency = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);

  const quantities = {
    totalQtyContracted: summe.totalQtyContracted,
    totalQtyUsed: summe.totalQtyUsed,
    percent: anteil(summe.totalQtyUsed, summe.totalQtyContracted),
  };

  const lastBase = meta.date || new Date().toISOString().split('T')[0];
  const nextOwner = intervalConfig.nextScheduledDate || getNextOccurrenceOf(lastBase, intervalConfig.preferredWeekdays || []);
  const nextDep1 = getNextOccurrenceOf(lastBase, dep1?.weekdays || []);
  const nextDep2 = getNextOccurrenceOf(lastBase, dep2?.weekdays || []);
  const totalAssignedDays = (intervalConfig.preferredWeekdays?.length || 0) +
      deputies.reduce((sum, rep) => sum + (rep.weekdays?.length || 0), 0);

  /*
   * Funktion einer Position umstellen (Dialog „Leistungen Details"). Nur
   * Admin, nicht im Lesemodus, nicht im Tochterprojekt — dessen Liste ist das
   * LV der MUTTER, hier geschrieben landete die Aenderung im eigenen Projekt.
   * Ueber onUpdate und nicht direkt in project_services: der offene Stand
   * schickt die Zuordnung bei jedem Speichern mit und ueberschriebe einen
   * Direkt-Update gleich wieder. Die Notiz macht die Umstellung nachvollziehbar.
   * „Assignment" ist hier die Funktion der LV-Position (Begehung, SiGe-Plan …),
   * nicht „Verantwortliche zuweisen" — bleibt deshalb Admin.
   */
  const canChangeAssignments = !isReadOnly && !linkedParent && isAdminRole(currentUser?.role);
  const handleChangeAssignment = (service: ServiceItem, next: ServiceAssignment) => {
      if (!canChangeAssignments) return;
      const services = changeServiceAssignment(globalData.services || [], service.id, next);
      if (!services) return;
      const note: Note = {
          id: crypto.randomUUID(),
          text: assignmentChangeNoteText(service, service.assignment, next),
          date: new Date().toISOString(),
          author: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'Benutzer',
          readBy: [currentUser?.id || ''],
          mentions: [],
      };
      onUpdate({ ...report, metadata: { ...meta, globalData: { ...globalData, services, notes: [note, ...(globalData.notes || [])] } } });
  };

  return (
    <>
    {/* isReadOnly blendet den Buchen-Knopf im Dialog aus; die Pruefung in
        onTrack bleibt als zweite Verteidigung stehen. */}
    {showDetailModal && <ServiceDetailsModal services={effectiveServices} serviceLog={detailServiceLog} onClose={() => setShowDetailModal(false)} onTrack={(service) => { if (!isReadOnly && onTrackService) { setShowDetailModal(false); onTrackService(service); } }} canSeeFinancials={canSeeFinancials} isReadOnly={isReadOnly || !onTrackService} onChangeAssignment={canChangeAssignments ? handleChangeAssignment : undefined} onOpenBestandteile={onOpenBestandteile ? (service) => { setShowDetailModal(false); onOpenBestandteile(service); } : undefined} bestandteileKontext={bestandteileKontext} bestandteileOffen={bestandteileOffen} />}
    {showNotesModal && (
        <NotesModal
            notes={visibleNotes}
            isReadOnly={isReadOnly}
            onClose={() => setShowNotesModal(false)}
            onAddNote={async (txt, mentions) => {
                // Die Notiz selbst verpufft im Lesemodus (onUpdate wird oben
                // verworfen), die Erwaehnungen nicht: createNotificationsForMentions
                // schreibt direkt und wuerde Benachrichtigungen zu einer Notiz
                // erzeugen, die es nie gab. Der Inline-Composer daneben ist
                // bereits gesperrt, das Modal war es nicht.
                if (isReadOnly) {
                    alert(readOnlyMessage('Die Notiz wurde nicht gespeichert und es wurde niemand benachrichtigt.'));
                    return;
                }
                const n: Note = {
                    id: crypto.randomUUID(), 
                    text: txt, 
                    date: new Date().toISOString(), 
                    author: currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'B.', 
                    readBy: [],
                    mentions: mentions
                }; 
                onUpdate({ ...report, metadata: { ...meta, globalData: { ...globalData, notes: [n, ...(globalData.notes || [])] } } }); 
                if (mentions && mentions.length > 0) {
                     await createNotificationsForMentions(
                        mentions,
                        n.id,
                        txt,
                        globalData.projectId || 'unknown',
                        report.metadata.projectName,
                        currentUser?.id || 'system',
                        currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : 'System'
                     );
                }
            }} 
            currentUser={currentUser} 
            availableUsers={availableUsers} 
        />
    )}
    
    <div data-tutorial="hub.kennzahlen" className="grid grid-cols-1 lg:grid-cols-3 gap-6">

       {/* 1. CONTAINER: bei SiFa die Betreuungskarte (Status Laufend/Ruht/
           Beendet, Betreuungsbeginn, Kennzahlen) — keine Phasen, kein
           Turnus, kein Wochenplan. Statuswechsel laufen ueber dieselbe
           handleStatusChange-Logik samt Archiv-Rueckfrage beim Beenden. */}
       {istSifa ? (
       <SifaBetriebsdatenKarte
          globalData={globalData}
          sifa={meta.sifa}
          kennzahlen={sifaKennzahlen}
          onStatusChange={(s) => { void handleStatusChange(s); }}
          archivPruefungLaeuft={archivPruefungLaeuft}
          darfArchivieren={darfArchivieren}
          onStartDateChange={(v) => handleDateChange('startDate', v)}
          onReportExemptChange={(v) => applyGlobalData({ reportExempt: v })}
          onShowHistoryModal={onShowHistoryModal}
       />
       ) : (
       <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 h-[700px] flex flex-col">
          <div className="flex justify-between items-start mb-4 shrink-0">
              <div>
                  <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Clock className="w-5 h-5 text-blue-600" /> Zeitplan & Intervall</h3>
                  <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-500">Global fällig:</span>
                      {globalData.status === 'planning' ? (
                          <span className="text-xs font-bold text-slate-400 italic bg-slate-100 px-2 py-0.5 rounded">Wartet auf Start</span>
                      ) : (
                          <span className="text-xs font-bold bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              {intervalConfig.nextScheduledDate ? new Date(intervalConfig.nextScheduledDate).toLocaleDateString() : 'Nicht geplant'}
                          </span>
                      )}
                  </div>
              </div>
              <Button variant="secondary" onClick={onShowHistoryModal} className="text-xs h-8 px-3" icon={<History className="w-3 h-3"/>}>Historie</Button>
          </div>

          <div className="space-y-5 flex-1 overflow-y-auto custom-scrollbar pr-1">
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Projektstart</label><input type="date" value={globalData.startDate || ''} onChange={(e) => handleDateChange('startDate', e.target.value)} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                      <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Gepl. Ende</label><input type="date" value={globalData.endDate || ''} onChange={(e) => handleDateChange('endDate', e.target.value)} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                  </div>

                  <div className="pt-3 border-t border-slate-200">
                      <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Planungsphase</label>
                      <div className="grid grid-cols-2 gap-3">
                          <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Von</label><input type="date" value={globalData.planningStartDate || ''} onChange={(e) => handleDateChange('planningStartDate', e.target.value)} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                          <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Bis</label><input type="date" value={globalData.planningEndDate || ''} onChange={(e) => handleDateChange('planningEndDate', e.target.value)} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                      </div>
                  </div>

                  <div className="pt-3 border-t border-slate-200">
                      <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Ausführungsphase</label>
                      <div className="grid grid-cols-2 gap-3">
                          <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Von</label><input type="date" value={globalData.executionStartDate || ''} onChange={(e) => handleDateChange('executionStartDate', e.target.value)} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                          <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Bis</label><input type="date" value={globalData.executionEndDate || ''} onChange={(e) => handleDateChange('executionEndDate', e.target.value)} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                      </div>
                  </div>

                  <div className="pt-3 border-t border-slate-200">
                      <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Weitere Zeiträume</label>

                      <div className="space-y-1.5 mb-2">
                          {(globalData.customDateRanges || []).length === 0 ? (
                              <div className="text-[11px] text-slate-400 italic py-1">Keine weiteren Zeiträume erfasst.</div>
                          ) : (
                              (globalData.customDateRanges || []).map(range => (
                                  <div key={range.id} className="bg-white border border-slate-200 rounded px-2 py-1.5">
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                          <span className="text-xs font-bold text-slate-700 truncate">{range.label}</span>
                                          <button
                                              onClick={() => handleRemoveCustomRange(range.id)}
                                              title="Zeitraum entfernen"
                                              className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0"
                                          >
                                              <Trash2 className="w-3 h-3" />
                                          </button>
                                      </div>
                                      <div className="text-[11px] font-mono text-slate-500">
                                          {new Date(range.startDate).toLocaleDateString('de-DE')} – {new Date(range.endDate).toLocaleDateString('de-DE')}
                                      </div>
                                  </div>
                              ))
                          )}
                      </div>

                      <div className="space-y-2">
                          <input
                              type="text"
                              value={newRange.label}
                              onChange={(e) => setNewRange({ ...newRange, label: e.target.value })}
                              placeholder="Bezeichnung (z. B. 14-täglich)"
                              className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none focus:border-blue-400"
                          />
                          <div className="grid grid-cols-2 gap-3">
                              <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Von</label><input type="date" value={newRange.startDate} onChange={(e) => setNewRange({ ...newRange, startDate: e.target.value })} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                              <div><label className="text-[11px] font-bold text-slate-400 uppercase block mb-1">Bis</label><input type="date" value={newRange.endDate} onChange={(e) => setNewRange({ ...newRange, endDate: e.target.value })} className="w-full text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 outline-none" /></div>
                          </div>
                          {newRange.startDate && newRange.endDate && newRange.startDate > newRange.endDate && (
                              <div className="text-[11px] font-bold text-red-500">Start muss vor Ende liegen.</div>
                          )}
                          <button
                              onClick={handleAddCustomRange}
                              disabled={!isValidCustomDateRangeDraft(newRange)}
                              className="w-full flex items-center justify-center gap-1 bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wide py-1.5 rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                              <Plus className="w-3 h-3" /> Zeitraum hinzufügen
                          </button>
                      </div>
                  </div>
              </div>

              <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Projektstatus</label>
                  <div className="flex bg-slate-50 p-1.5 rounded-xl border border-slate-100 gap-1">
                      {statusOptionen.map(s => {
                          const isActive = globalData.status === s;
                          const labels: Record<ProjectStatus, string> = { planning: 'Planung', active: 'Ausführung', on_hold: 'Ruht', completed: 'Beendet', finished: 'Archiv' };
                          return <button key={s} onClick={() => { void handleStatusChange(s); }} disabled={archivPruefungLaeuft} className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all border disabled:opacity-50 disabled:cursor-wait ${isActive ? 'bg-white border-blue-200 text-blue-700 shadow-sm' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>{s === 'finished' && archivPruefungLaeuft ? 'Prüfe…' : labels[s]}</button>;
                      })}
                  </div>
                  {statusHinweis && <p className="text-[11px] text-slate-400 mt-1.5">{statusHinweis}</p>}
              </div>

              {/* SiFa-Dauerprojekt: kein Begehungsintervall — der Block
                  wuerde sonst „Wöchentlich" vorschlagen (Vorgabe im Hub). */}
              {report.metadata.projectType !== 'sifa' && <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2 flex items-center gap-2"><Settings2 className="w-3 h-3" /> Frequenz-Einstellung</label>
                  <div className="grid grid-cols-2 gap-2">
                      {[{ id: 'daily', label: 'Täglich' }, { id: 'weekly', label: 'Wöchentlich' }, { id: 'biweekly', label: '14-tägig' }, { id: 'twice_monthly', label: '2x Monat' }, { id: 'monthly', label: 'Monatlich' }, { id: 'custom', label: 'Benutzerd.' }].map((opt) => (
                        <button key={opt.id} onClick={() => handleIntervalChange(opt.id as IntervalType)} className={`px-2 py-2 rounded-lg text-[11px] font-bold uppercase border transition-all ${intervalConfig.type === opt.id ? 'bg-blue-600 text-white border-blue-700 shadow-sm' : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-slate-300'}`}>{opt.label}</button>
                      ))}
                  </div>
                  {intervalConfig.type === 'custom' && (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                        <span className="text-xs font-medium text-blue-700">Tage:</span>
                        <input type="number" value={customDays} onChange={(e) => handleIntervalChange('custom', parseInt(e.target.value) || 1)} className="w-12 bg-white border border-blue-200 rounded px-1 text-center font-bold text-xs" />
                    </div>
                  )}
              </div>}

              {/* WOCHENPLAN MATRIX */}
              <div className="pt-2">
                  <div className="flex justify-between items-end mb-3">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Wochenplan & Ressourcen-Split</label>
                      <span className="text-[11px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100">{totalAssignedDays} Tage/Woche</span>
                  </div>
                  
                  <div className="space-y-5 bg-slate-50/50 p-4 rounded-2xl border border-slate-100">
                      <div className="space-y-2">
                          <div className="flex items-center gap-2"><User className="w-3 h-3 text-blue-600" /><span className="text-[11px] font-black text-slate-700 uppercase truncate">{meta.coordinator}</span></div>
                          <div className="grid grid-cols-7 gap-1">
                              {WEEKDAYS.map(day => {
                                  const isSelected = (intervalConfig.preferredWeekdays || []).includes(day.value);
                                  const isTaken = (dep1?.weekdays?.includes(day.value) || dep2?.weekdays?.includes(day.value));
                                  return (<button key={day.value} onClick={() => handleWeekdaySplit('owner', day.value)} className={`h-8 rounded-lg border transition-all flex items-center justify-center flex-col ${isSelected ? 'bg-blue-600 border-blue-700 text-white shadow-sm' : (isTaken ? 'bg-slate-200 border-slate-200 opacity-20 cursor-not-allowed' : 'bg-white border-slate-200 hover:border-blue-400')}`}><span className="text-[10px] font-black uppercase leading-none mb-0.5">{day.label}</span>{isSelected && <Check className="w-2.5 h-2.5" />}</button>);
                              })}
                          </div>
                      </div>

                      {hasDeputy1 && (
                          <div className="space-y-2 pt-2 border-t border-slate-200">
                              <div className="flex items-center gap-2"><UserCheck className="w-3 h-3 text-emerald-600" /><span className="text-[11px] font-black text-emerald-700 uppercase truncate">{dep1?.name}</span></div>
                              <div className="grid grid-cols-7 gap-1">
                                  {WEEKDAYS.map(day => {
                                      const isSelected = (dep1?.weekdays || []).includes(day.value);
                                      const isTaken = (intervalConfig.preferredWeekdays?.includes(day.value) || dep2?.weekdays?.includes(day.value));
                                      return (<button key={day.value} onClick={() => handleWeekdaySplit('deputy', day.value)} className={`h-8 rounded-lg border transition-all flex items-center justify-center flex-col ${isSelected ? 'bg-emerald-600 border-emerald-700 text-white shadow-sm' : (isTaken ? 'bg-slate-200 border-slate-200 opacity-20 cursor-not-allowed' : 'bg-white border-slate-200 hover:border-emerald-400')}`}><span className="text-[10px] font-black uppercase leading-none mb-0.5">{day.label}</span>{isSelected && <Check className="w-2.5 h-2.5" />}</button>);
                                  })}
                              </div>
                          </div>
                      )}

                      {hasDeputy2 && (
                          <div className="space-y-2 pt-2 border-t border-slate-200">
                              <div className="flex items-center gap-2"><UserCheck className="w-3 h-3 text-indigo-600" /><span className="text-[11px] font-black text-indigo-700 uppercase truncate">{dep2?.name}</span></div>
                              <div className="grid grid-cols-7 gap-1">
                                  {WEEKDAYS.map(day => {
                                      const isSelected = (dep2?.weekdays || []).includes(day.value);
                                      const isTaken = (intervalConfig.preferredWeekdays?.includes(day.value) || dep1?.weekdays?.includes(day.value));
                                      return (<button key={day.value} onClick={() => handleWeekdaySplit('deputy2', day.value)} className={`h-8 rounded-lg border transition-all flex items-center justify-center flex-col ${isSelected ? 'bg-indigo-600 border-indigo-700 text-white shadow-sm' : (isTaken ? 'bg-slate-200 border-slate-200 opacity-20 cursor-not-allowed' : 'bg-white border-slate-200 hover:border-indigo-400')}`}><span className="text-[10px] font-black uppercase leading-none mb-0.5">{day.label}</span>{isSelected && <Check className="w-2.5 h-2.5" />}</button>);
                                  })}
                              </div>
                          </div>
                      )}

                      <div className="mt-2 pt-3 border-t border-slate-200 space-y-1.5">
                           <div className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-100"><span className="text-[10px] font-bold text-slate-500 uppercase">Nächste Termine</span><Clock className="w-3 h-3 text-slate-400" /></div>
                           <div className="flex items-center justify-between text-[11px]"><span className="text-slate-600 truncate max-w-[150px]">{meta.coordinator}</span><span className="font-mono font-bold text-blue-600">{nextOwner ? new Date(nextOwner).toLocaleDateString('de-DE') : '-'}</span></div>
                           {hasDeputy1 && (<div className="flex items-center justify-between text-[11px]"><span className="text-emerald-700 truncate max-w-[150px]">{dep1?.name}</span><span className="font-mono font-bold text-emerald-600">{nextDep1 ? new Date(nextDep1).toLocaleDateString('de-DE') : '-'}</span></div>)}
                           {hasDeputy2 && (<div className="flex items-center justify-between text-[11px]"><span className="text-indigo-700 truncate max-w-[150px]">{dep2?.name}</span><span className="font-mono font-bold text-indigo-600">{nextDep2 ? new Date(nextDep2).toLocaleDateString('de-DE') : '-'}</span></div>)}
                      </div>
                  </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                   <Button onClick={() => downloadIcs({ id: globalData.projectId, name: report.metadata.projectName, data: report })} variant="secondary" className="w-full flex items-center justify-center gap-2 text-xs h-9 border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 font-bold uppercase tracking-wide shadow-sm" icon={<Calendar className="w-4 h-4"/>}>Kalender übertragen</Button>
              </div>

              <div className="pt-3 border-t border-slate-200">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                      <input
                          type="checkbox"
                          checked={globalData.reportExempt === true}
                          onChange={(e) => applyGlobalData({ reportExempt: e.target.checked })}
                          className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>
                          <span className="text-xs font-bold text-slate-700 block">Keine Begehungen/Berichte für dieses Projekt</span>
                          <span className="text-[11px] text-slate-400 block mt-0.5">Unterdrückt Begehungs-Prüfungen und -Erinnerungen (nur Leistungsbuchung).</span>
                      </span>
                  </label>
              </div>
          </div>
       </div>
       )}

       {/* 2. CONTAINER: Budget & Services */}
       <div data-tutorial="leistung.karte" className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[700px]">
           <div className="flex justify-between items-start mb-4 shrink-0">
                <div>
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> {linkedParent ? 'Budget (Mutterprojekt)' : 'Leistung & Budget'}</h3>
                    <p className="text-xs text-slate-500 mt-1">Verbrauchter Anteil</p>
                </div>
                <Button variant="secondary" onClick={() => setShowDetailModal(true)} className="text-xs h-8 px-3">Details</Button>
           </div>
           
           {linkedParent && (
               <div className="mb-4 bg-indigo-50 border border-indigo-100 p-3 rounded-lg flex items-center gap-2 text-xs text-indigo-800">
                   <LinkIcon className="w-4 h-4 shrink-0" />
                   <span>Daten werden vom Mutterprojekt <strong>{linkedParent.metadata.projectName}</strong> geerbt.</span>
               </div>
           )}

           {canSeeFinancials ? (
               <div className="space-y-4 mb-4 shrink-0">
                   <div className="flex justify-between items-end">
                       <div><div className="text-3xl font-black text-slate-800 tracking-tight">{budgetPercent.toFixed(1)}%</div><div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Genutzt</div></div>
                       <div className="text-right"><div className="text-sm font-bold text-slate-700">{currency(financials.totalUsed)}</div><div className="text-[11px] text-slate-400">von {currency(financials.totalContracted)}</div></div>
                   </div>
                   <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden shadow-inner"><div className={`h-full transition-all duration-1000 ease-out rounded-full ${budgetPercent > 100 ? 'bg-red-500' : (budgetPercent > 85 ? 'bg-orange-400' : 'bg-emerald-500')}`} style={{ width: `${Math.min(100, budgetPercent)}%` }}></div></div>
               </div>
           ) : (
              <div className="space-y-4 mb-4 shrink-0">
                <div className="flex justify-between items-end">
                  <div><div className="text-3xl font-black text-slate-800 tracking-tight">{quantities.percent.toFixed(1)}%</div><div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Fortschritt (Menge)</div></div>
                  <div className="text-right"><div className="text-sm font-bold text-slate-700">{quantities.totalQtyUsed}</div><div className="text-[11px] text-slate-400">von {quantities.totalQtyContracted} Einheiten</div></div>
                </div>
                <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden shadow-inner"><div className={`h-full transition-all duration-1000 ease-out rounded-full ${quantities.percent > 100 ? 'bg-red-500' : (quantities.percent > 85 ? 'bg-orange-400' : 'bg-emerald-500')}`} style={{ width: `${Math.min(100, quantities.percent)}%` }}></div></div>
              </div>
           )}
           <div className="pt-2 border-t border-slate-100 flex-1 overflow-y-auto custom-scrollbar">
               {/* MONATSPAUSCHALE: der Monatsvorschlag steht dort, wo der
                   Betrieb ohnehin Leistungen bucht. Nur Ansage + Knopf —
                   gebucht wird ausschliesslich im Dialog auf Klick. */}
               {onBookFlatMonth && flatMonthSuggestions.length > 0 && (
                   <div data-tutorial="leistung.pauschalmonat" className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3">
                       <div className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                           <CalendarClock className="w-3.5 h-3.5" /> Monatspauschale
                       </div>
                       <div className="space-y-1">
                           {flatMonthSuggestions.slice(0, 4).map(s => (
                               <div key={`${s.flatServiceId}-${s.monthKey}`} className="flex items-center justify-between gap-2 text-xs text-indigo-900">
                                   <span className="min-w-0 truncate">{s.monthLabel} noch nicht gebucht</span>
                                   {!isReadOnly && (
                                       <button
                                           onClick={() => setFlatBookingMonthKey(s.monthKey)}
                                           className="shrink-0 text-[11px] font-bold text-indigo-700 hover:text-indigo-900 underline underline-offset-2"
                                       >
                                           buchen
                                       </button>
                                   )}
                               </div>
                           ))}
                           {flatMonthSuggestions.length > 4 && (
                               <div className="text-[11px] text-indigo-700">+ {flatMonthSuggestions.length - 4} weitere Monate offen</div>
                           )}
                       </div>
                   </div>
               )}
               <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Schnellzugriff Leistung</h4>
               <div className="space-y-2">
                   {effectiveServices.map((service, positionIndex) => {
                       // PAKETPOSITION: Plakette „Bestandteile 4/5" und ein Klick
                       // oeffnet die Bestandteile statt der Schnellbuchung
                       // (Kernregel 7). Der Stand kommt aus dem Projekt, nach
                       // einer Handlung im Dialog aus der Ueberschreibung des Hubs.
                       const paket = hatBestandteile(service);
                       const plakette = paket ? plaketteFuer(service, bestandteileOffen) : null;
                       // Daneben die Stunden gegen das Kontingent (intern, rot wenn darueber).
                       const stundenPlakette = paket ? stundenPlaketteFuer(service, bestandteileOffen) : null;
                       const klickbar = paket ? !!onOpenBestandteile : (!isReadOnly && !!onTrackService);
                       return (
                       <div key={service.id} {...anker('leistung.position', positionIndex === 0)} className={`flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 transition-colors group ${klickbar ? 'hover:border-blue-200 cursor-pointer' : ''}`} onClick={() => { if (paket) { onOpenBestandteile?.(service); return; } if (!isReadOnly && onTrackService) onTrackService(service); }}>
                           <div className="min-w-0 pr-2"><ClampedText text={service.title} lines={2} className="text-xs font-bold text-slate-700 break-words" /><div className="text-[11px] text-slate-400 font-mono flex items-center gap-1.5">{service.lvPosition}{/* NEP: nicht beauftragt — muss hier erkennbar sein, sonst sieht die Eventualposition aus wie jede andere */}{isNepService(service) && <span className="text-[10px] font-black text-amber-800 bg-amber-100 border border-amber-200 px-1 rounded font-sans" title={NEP_HINT}>{NEP_BADGE}</span>}</div>{flatHints.has(service.id) && <div className="text-[10px] font-medium text-indigo-600 truncate" title={flatHints.get(service.id)}>{flatHints.get(service.id)}</div>}{(plakette || stundenPlakette) && <div className="mt-1 flex flex-wrap gap-1">{plakette && <span className={`inline-block text-[11px] font-semibold border px-1.5 rounded ${PLAKETTEN_KLASSE[plakette.ton]}`} title={plakette.titel}>{plakette.text}</span>}{stundenPlakette && <span className={`inline-block text-[11px] font-semibold tabular-nums border px-1.5 rounded ${PLAKETTEN_KLASSE[stundenPlakette.ton]}`} title={stundenPlakette.titel}>{stundenPlakette.text}</span>}</div>}</div>
                           <div className="flex items-center gap-2 shrink-0"><span className="text-[11px] font-medium text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200" title={isNepService(service) ? `Angebotsmenge ${service.quantityContracted} ${service.unit} – zählt erst mit der Buchung` : undefined}>{isNepService(service) ? `${service.quantityUsed} gebucht` : `${service.quantityUsed}/${service.quantityContracted}`}</span>{paket ? (onOpenBestandteile && <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center" title="Bestandteile ansehen"><ListChecks className="w-3.5 h-3.5" /></div>) : (onTrackService && <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity"><Plus className="w-3.5 h-3.5" /></div>)}</div>
                       </div>
                       );
                   })}
               </div>
           </div>
       </div>

       {/* 3. CONTAINER: Message Center */}
       <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col h-[700px]">
           <div className="flex justify-between items-start mb-4 shrink-0">
               <div><h3 className="text-lg font-bold text-slate-800 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-indigo-600" /> Mitteilungszentrale</h3><p className="text-xs text-slate-500 mt-1">Interne Notizen & Updates</p></div>
               <Button variant="secondary" onClick={() => setShowNotesModal(true)} className="text-xs h-8 px-3">Details</Button>
           </div>
           
           <div className="flex-1 bg-slate-50 rounded-xl border border-slate-100 p-3 overflow-y-auto custom-scrollbar flex flex-col gap-2" onClick={() => setShowQuickMentions(false)}>
               {visibleNotes.length > 0 ? (
                   visibleNotes.map((note) => (
                       <div key={note.id} className="bg-white p-3 rounded-lg border border-slate-200 text-xs shadow-sm"><div className="flex justify-between text-[11px] text-slate-400 mb-1.5"><span className="font-bold text-slate-600 flex items-center gap-1"><User className="w-3 h-3"/> {note.author}</span><span>{new Date(note.date).toLocaleDateString()}</span></div><p className="text-slate-700 leading-snug">{note.text}</p></div>
                   ))
               ) : (<div className="flex flex-col items-center justify-center h-full text-slate-400 italic text-xs"><MessageSquare className="w-6 h-6 mb-2 opacity-20" />Keine Nachrichten vorhanden.</div>)}
           </div>
           
           <div className="mt-3 relative shrink-0">
               {showQuickMentions && filteredQuickUsers.length > 0 && (
                  <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in slide-in-from-bottom-2 duration-200">
                      <div className="bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-500 uppercase border-b border-slate-100">Erwähnen...</div>
                      <div className="max-h-40 overflow-y-auto">
                          {filteredQuickUsers.map(u => (
                              <div key={u.id} onClick={() => handleQuickMentionSelect(u)} className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-xs flex items-center justify-between border-b border-slate-50 last:border-0 group transition-colors">
                                  <div className="flex items-center gap-2">
                                      <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[11px] font-bold">{u.first_name?.[0]}{u.last_name?.[0]}</div>
                                      <span className="font-bold text-slate-700">{u.first_name} {u.last_name}</span>
                                  </div>
                                  <AtSign className="w-3 h-3 text-slate-300 group-hover:text-blue-500" />
                              </div>
                          ))}
                      </div>
                  </div>
               )}
               {!isReadOnly ? (
                   <div className="flex gap-2">
                       <input ref={quickInputRef} type="text" value={newNote} onChange={handleQuickInputChange} onKeyDown={(e) => e.key === 'Enter' && !showQuickMentions && handleAddNoteLocal()} placeholder="Notiz verfassen (@ für Erwähnung)..." className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2 outline-none focus:border-blue-400 bg-slate-50 focus:bg-white transition-colors" />
                       <button onClick={handleAddNoteLocal} disabled={!newNote.trim()} className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"><Send className="w-4 h-4" /></button>
                   </div>
               ) : (
                   <div className="text-center py-2.5 bg-slate-100 rounded-xl text-xs font-semibold text-slate-400 select-none border border-slate-200 w-full mb-1">
                       Notizerstellung im Lese-Modus deaktiviert
                   </div>
               )}
           </div>
       </div>
    </div>
    
    {/* onFinished muss das Modal schließen: bei 'success' ist das X ausgeblendet
        (EmailModal), ein No-op ließe den Nutzer nur noch per App-Reload heraus —
        und damit ohne Erfolgsbestätigung. Die Ansicht selbst ist bereits
        aktuell, weil onUpdate den gebuchten Report nach oben durchreicht. */}
    {showEmailModal && (
        <EmailModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} report={report} settings={report.settings} onUpdate={(r) => { onUpdate(r); }} projectId={globalData.projectId} activeSectionId={report.metadata.sectionId} precapturedHours={precapturedHours} additionalAddons={selectedAddons} precapturedServiceId={precapturedServiceId} onFinished={() => setShowEmailModal(false)} userProfile={currentUser} />
    )}

    {/* MONATSPAUSCHALE: der Buchungsdialog des Monatsvorschlags. Menge
        vorbelegt mit 1 und frei ueberschreibbar; der Schluessel-Riegel laeuft
        im Handler (useReportActions) erneut gegen den aktuellen Stand. */}
    {flatBookingMonthKey && onBookFlatMonth && (
        <FlatMonthBookingDialog
            subtitle={meta.projectName}
            suggestions={flatMonthSuggestions}
            initialMonthKey={flatBookingMonthKey}
            onBook={(s, quantity) => onBookFlatMonth(s.flatServiceId, s.monthKey, quantity)}
            onClose={() => setFlatBookingMonthKey(null)}
        />
    )}

    {/* Die Rückfrage vor dem Archivieren. Sie räumt notfalls die
        Dokumentenablage und meldet erst danach den Statuswechsel — geht das
        Räumen schief, bleibt das Projekt offen und der Grund sichtbar. */}
    {archivDialogOffen && globalData.projectId && (
        <ArchivDokumenteDialog
            projectId={globalData.projectId}
            benutzerName={[currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || currentUser?.email}
            istAdmin={rechte.hat('dokumente.nur_admin')}
            onAbbrechen={() => setArchivDialogOffen(false)}
            onAbschliessen={(entscheidung) => {
                setArchivDialogOffen(false);
                applyStatusChange('finished', entscheidung);
            }}
        />
    )}

    {beendenRueckfrage && (
        <div className="fixed inset-0 z-[110] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                <h3 className="font-bold text-lg text-slate-800">Projekt beenden?</h3>
                <p className="text-sm text-slate-600 mt-2">
                    Das Projekt verschwindet danach aus der Projektliste; die Buchhaltung stellt die Schlussrechnung.{' '}
                    {/* Die Projektverwaltung bleibt Admin-Bereich. */}
                    {isAdminRole(currentUser?.role)
                        ? 'Zurückholen können Sie es über die Projektverwaltung.'
                        : 'Zurückholen kann es nur ein Administrator.'}
                </p>
                <div className="mt-5 flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setBeendenRueckfrage(false)}>Abbrechen</Button>
                    <Button variant="primary" onClick={() => { setBeendenRueckfrage(false); applyStatusChange('completed'); }}>Beenden</Button>
                </div>
            </div>
        </div>
    )}
    </>
  );
};
